import { Delete } from '@mui/icons-material';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Link,
	TextField,
} from '@mui/material';
import { FC, useState, useRef } from 'react';
import { FileDrop } from 'react-file-drop';
import { uploadSalesReportRequest } from 'redux/reducers/settings/reducer';
import { useDispatch } from 'react-redux';
import { IRequestWithPageSortFilterSearch } from 'services/constants/types';
import { WarningAlert } from 'ui/WarningAlert';

interface UploadSalesReportDialogProps {
	open: boolean;
	onClose: () => void;
	reloadBody: IRequestWithPageSortFilterSearch;
}

export const UploadSalesReportDialog: FC<UploadSalesReportDialogProps> = ({
	open,
	onClose,
	reloadBody,
}) => {
	const dispatch = useDispatch();
	const [files, setFiles] = useState<File[]>([]);
	const [noSales, setNoSales] = useState(false);
	const [comments, setComments] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleEraseForm = () => {
		setFiles([]);
		setNoSales(false);
		setComments('');
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			setFiles(Array.from(event.target.files));
		}
	};

	const handleUpload = () => {
		if (files.length > 0 || noSales) {
			const formData = new FormData();
			files.forEach((file) => {
				formData.append('File', file);
			});
			formData.append('Comments', comments);
			formData.append('NoSales', noSales.toString());
			dispatch(uploadSalesReportRequest({ formData, reloadBody }));
			handleEraseForm();
			onClose();
		}
	};

	const onTargetClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const onDrop = (droppedFiles: FileList | null, event: React.DragEvent<HTMLDivElement>) => {
		if (droppedFiles && droppedFiles.length > 0) {
			setFiles(Array.from(droppedFiles));
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const handleClose = () => {
		handleEraseForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle
				color="primary"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				Upload Sales Report
			</DialogTitle>
			<DialogContent>
				<Box>
					<WarningAlert>
						Kindly ensure that the sales report has been created in accordance with the guidelines
						provided in the{' '}
						<Link
							sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 'bold' }}
							href="https://help.easproject.com/hc/en-gb/articles/5022170254111-Is-EAS-IOSS-usable-with-other-platforms-than-supported-by-direct-integration"
							target="_blank"
							rel="noreferrer"
						>
							help documentation
						</Link>
					</WarningAlert>
					<Box sx={{ mb: 2, p: '10px 0 0 0' }}>
						<TextField
							label="Comments"
							multiline
							rows={2}
							fullWidth
							value={comments}
							onChange={(e) => setComments(e.target.value)}
						/>
					</Box>
					{!noSales && files.length === 0 && (
						<FileDrop onDrop={onDrop} onTargetClick={onTargetClick}>
							Drag Files or Click to Browse
							<div style={{ fontSize: '.8rem', marginTop: '15px' }}>
								Please, attach file that proves correction reason.
								<br />
								If you need to attach several files, please,
								<br />
								put them in the zip file and attach as a single attachment.
								<br />
								<b>File size should not exceed 10 Mb.</b>
							</div>
						</FileDrop>
					)}

					{!noSales && files.length > 0 && (
						<Box>
							{files.map((file) => (
								<Box
									key={file.name}
									sx={{
										fontSize: '1.2rem',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										color: 'primary.main',
										mt: 2,
									}}
								>
									<Box color={file.size > 10_000_000 ? 'error.main' : 'auto'}>
										<b>{file.name}</b>,{' '}
										{file.size > 1_000_000 ? (
											<span>{(file.size / 1_000_000).toFixed(3)} MB </span>
										) : file.size > 1000 ? (
											<span>{(file.size / 1000).toFixed(3)} KB </span>
										) : (
											<span>{file.size} B</span>
										)}
									</Box>
									<IconButton
										onClick={() => {
											setFiles(files.filter((_, i) => i !== files.indexOf(file)));
										}}
										color="primary"
									>
										<Delete />
									</IconButton>
								</Box>
							))}
							{files.some((file) => file.size > 10_000_000) && (
								<Box style={{ fontSize: '.9rem', marginTop: '10px' }} color="error.main">
									<b>File size should not exceed 10 MB!</b>
								</Box>
							)}
						</Box>
					)}
					<input
						ref={fileInputRef}
						type="file"
						accept=".csv,.xls,.xlsx"
						onChange={handleFileChange}
						style={{ display: 'none' }}
					/>
					<Box>
						<FormControlLabel
							control={
								<Checkbox checked={noSales} onChange={(e) => setNoSales(e.target.checked)} />
							}
							label="No sales for the reporting month"
						/>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button
					onClick={handleUpload}
					variant="contained"
					color="primary"
					disabled={files.length === 0 && !noSales}
				>
					Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};
